import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import CasesRelacionados from '@/pages/cases/components/CasesRelacionados';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const IMG_HERO = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/GALERIA-TECNICA/SUBSTITUICAO-MOTOR-HIDRAULICO.webp';
const IMG_HERO_AI = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/HERO/HERO-HIDRAULICA-INDUSTRIAL.webp';
const IMG_UNIDADE = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/GALERIA-TECNICA/UNIDADE-HIDRAULICA-MANUTENCAO.webp';
const IMG_LIMPEZA = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/GALERIA-TECNICA/LIMPEZA-UNIDADE-HIDRAULICA.webp';
const IMG_HASTE = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/GALERIA-TECNICA/HASTE-CILINDRO.webp';
const IMG_ACUMULADOR = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/GALERIA-TECNICA/ACUMULADOR-HIDRAULICO-PRE-CARGA.webp';
const IMG_VEDACOES = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/GALERIA-TECNICA/SUBSTITUICAO-VEDACOES-UNIDADE-HIDRAULICA.webp';
const IMG_PARADA = '/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/GALERIA-TECNICA/PARADA-GERAL-SUZANO-2014.webp';

const services = [
  { title: 'Diagnóstico de Sistemas Hidráulicos', desc: 'Avaliação técnica completa de sistemas hidráulicos para identificar vazamentos, falhas e perda de performance.' },
  { title: 'Substituição de Componentes', desc: 'Troca de componentes hidráulicos desgastados ou danificados com peças de qualidade equivalente.' },
  { title: 'Troca de Mangueiras', desc: 'Substituição de mangueiras hidráulicas com materiais adequados à pressão e fluido de trabalho.' },
  { title: 'Ajustes Operacionais', desc: 'Calibração e ajustes de parâmetros operacionais para otimização de performance dos sistemas.' },
  { title: 'Inspeções Hidráulicas', desc: 'Inspeções preventivas e corretivas para identificar condições e prever necessidades de intervenção.' },
  { title: 'Correção de Vazamentos', desc: 'Identificação e reparo de vazamentos em conexões, vedações, mangueiras e componentes hidráulicos.' },
  { title: 'Montagem Hidráulica', desc: 'Montagem e conexão de sistemas hidráulicos seguindo especificações técnicas e normas de segurança.' },
  { title: 'Apoio Técnico Especializado', desc: 'Suporte técnico especializado em campo para diagnóstico, resolução e consultoria hidráulica.' },
];

const segments = [
  'Papel e Celulose',
  'Mineração',
  'Portos',
  'Siderurgia',
  'Energia',
];

const galleryItems = [
  {
    img: IMG_UNIDADE,
    label: 'Unidades Hidráulicas',
    alt: 'Manutenção especializada em unidades hidráulicas industriais com foco em desempenho e confiabilidade operacional',
  },
  {
    img: IMG_LIMPEZA,
    label: 'Limpeza e Inspeção',
    alt: 'Processo de limpeza técnica e inspeção detalhada dos componentes hidráulicos',
  },
  {
    img: IMG_HASTE,
    label: 'Cilindros Hidráulicos',
    alt: 'Inspeção dimensional e recuperação de hastes de cilindros hidráulicos industriais',
  },
  {
    img: IMG_ACUMULADOR,
    label: 'Acumuladores Hidráulicos',
    alt: 'Verificação de pré-carga e avaliação técnica de acumuladores hidráulicos industriais',
  },
  {
    img: IMG_VEDACOES,
    label: 'Sistemas de Vedação',
    alt: 'Substituição de elementos de vedação para eliminação de vazamentos e aumento da vida útil dos equipamentos',
  },
  {
    img: IMG_PARADA,
    label: 'Atuação em Paradas Industriais',
    alt: 'Atuação em paradas industriais de grande porte com equipes especializadas em sistemas hidráulicos',
  },
  {
    img: IMG_HERO,
    label: 'Substituição de Motor Hidráulico',
    alt: 'Substituição e recuperação de motores hidráulicos industriais para aumento da confiabilidade operacional',
  },
];

export default function HidraulicaIndustrialPage() {
  useSeo({
    title: 'Hidráulica Industrial | Serviços de Campo | Pilar',
    description: 'Manutenção, diagnóstico e recuperação de sistemas hidráulicos industriais em campo. Troca de mangueiras, correção de vazamentos, substituição de componentes e apoio técnico especializado.',
    keywords: 'hidráulica industrial, manutenção hidráulica industrial, serviços hidráulicos em campo, diagnóstico hidráulico, troca de mangueiras, correção de vazamentos, sistemas hidráulicos, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/hidraulica-industrial',
    ogUrl: 'https://www.pilarmanutencao.com.br/hidraulica-industrial',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={IMG_HERO_AI}
              alt="Serviços de hidráulica industrial em campo com manutenção de unidades hidráulicas bombas motores válvulas e mangueiras - Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Serviços de Campo</span>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Hidráulica</span> Industrial
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Atuamos em campo na manutenção, diagnóstico e recuperação de sistemas hidráulicos industriais.
              </p>
            </div>
          </div>
        </section>

        {/* PRINCIPAIS SERVIÇOS */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Serviços</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEGMENTOS ATENDIDOS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Atendimento Regional */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Atendimento Regional no Espírito Santo</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[{ city: 'Aracruz', slug: 'aracruz', desc: 'Celulose, Florestal, Portos e Mineração' }, { city: 'Serra', slug: 'serra', desc: 'Siderurgia, Metal Mecânica e Mineração' }, { city: 'Vitória', slug: 'vitoria', desc: 'Portos, Logística e Mineração' }, { city: 'Linhares', slug: 'linhares', desc: 'Celulose, Florestal e Agroindústria' }].map((item) => (
                  <Link key={item.slug} to={`/hidraulica-industrial-${item.slug}`} className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-gp-yellow/30 p-5 bg-gp-graphite transition-all duration-300 hover:-translate-y-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow transition-colors duration-300">
                        <i className="ri-map-pin-line text-gp-yellow group-hover:text-gp-graphite w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <span className="font-display text-sm font-bold text-white uppercase group-hover:text-gp-yellow transition-colors duration-300">{item.city}</span>
                    </div>
                    <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Galeria Técnica</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {galleryItems.map((item, index) => (
                <div key={index} className="group relative overflow-hidden rounded-lg bg-gp-graphite border border-gp-graphite-lighter">
                  <div className="h-[320px] flex items-center justify-center p-3">
                    <img
                      src={item.img}
                      alt={item.alt}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-gp-graphite/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-white text-sm font-semibold uppercase tracking-wider">{item.label}</p>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto {index + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de apoio em hidráulica industrial?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica e conte com o Grupo Pilar para apoiar sua operação com serviços hidráulicos de campo.
                </p>
                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Apoio Hidráulico</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        <CasesRelacionados serviceSlugs={['hidraulica-industrial']} />

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}